import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Dropdown } from 'react-bootstrap'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import * as reports from '../../_augmentt/ducks/reports'
import { getSelectedCustomerId, getSelectedCustomerName } from '../../_augmentt/selectors/customers'
import { getUserOrganization } from '../../_augmentt/selectors/user'
import { useGetAiReadinessReportQuery, useGetConditionalAccessPoliciesQuery } from '../../_augmentt/services/secure'
import AIRiskReportRenderer from '../pages/home/AIRiskReport/AIRiskReportRenderer'
import styles from '../pages/home/AIRiskReport/AIRiskReport.module.scss'
import { AI_RISK_REPORT_EXPORT_ERROR_TYPE } from '../pages/home/AIRiskReport/data/constants'
import { AI_RISK_REPORT_RENDER_MODES } from '../pages/home/AIRiskReport/data/renderMode'
import downloadAIRiskReportPDF from '../pages/home/AIRiskReport/downloadAIRiskReportPDF'
import { emitReportExportAuditLog } from '../util/audit.util'

const EXPORT_RENDER_FRAME_COUNT = 2
const AI_RISK_REPORT_AUDIT = {
  REPORT_NAME: 'AI Risk Report',
  REPORT_TYPE: 'AI_RISK_REPORT',
  SOURCE: 'AI_RISK_REPORT',
  FORMAT: 'pdf',
  OUTPUT_COUNT: 1,
  COMPONENT: 'AIRiskReportExportWidget',
}

const waitForAnimationFrame = () =>
  new Promise((resolve) => {
    const requestFrame = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : setTimeout
    requestFrame(resolve)
  })

const waitForExportRender = async () => {
  for (let frame = 0; frame < EXPORT_RENDER_FRAME_COUNT; frame += 1) {
    await waitForAnimationFrame() // eslint-disable-line no-await-in-loop
  }
}

const getPublishedBrandingLogo = (brandingData) =>
  brandingData?.logo_light && brandingData?.publish_logo ? brandingData.logo_light : undefined

function AIRiskReportExportWidget() {
  const intl = useIntl()
  const dispatch = useDispatch()
  const location = useLocation()
  const exportRef = useRef(null)
  const customerId = useSelector(getSelectedCustomerId)
  const customerName = useSelector(getSelectedCustomerName)
  const brandingData = useSelector(({ branding }) => branding.data)
  const organizationId = useSelector(getUserOrganization)
  const [isExporting, setIsExporting] = useState(false)
  const [isExportRendererMounted, setIsExportRendererMounted] = useState(false)
  const {
    currentData: report,
    isLoading: queryIsLoading,
    isFetching,
    isError,
  } = useGetAiReadinessReportQuery(customerId, { skip: !customerId })
  const isLoading = queryIsLoading || isFetching
  const {
    currentData: conditionalAccessPolicies = [],
    isLoading: conditionalAccessQueryIsLoading,
    isFetching: conditionalAccessIsFetching,
    isError: conditionalAccessPoliciesIsError,
  } = useGetConditionalAccessPoliciesQuery(customerId, { skip: !customerId })
  const conditionalAccessPoliciesIsLoading = conditionalAccessQueryIsLoading || conditionalAccessIsFetching

  useEffect(() => {
    dispatch(reports.actions.setReportError({ status: false, type: null, data: null }))
  }, [customerId, dispatch])

  const emitExportAuditLog = useCallback(() => {
    if (!organizationId) return

    emitReportExportAuditLog({
      organizationId,
      customerId,
      customerName,
      reportName: AI_RISK_REPORT_AUDIT.REPORT_NAME,
      reportType: AI_RISK_REPORT_AUDIT.REPORT_TYPE,
      source: AI_RISK_REPORT_AUDIT.SOURCE,
      format: AI_RISK_REPORT_AUDIT.FORMAT,
      outputCount: AI_RISK_REPORT_AUDIT.OUTPUT_COUNT,
      path: location.pathname,
      component: AI_RISK_REPORT_AUDIT.COMPONENT,
    })
  }, [customerId, customerName, location.pathname, organizationId])

  const handleExport = useCallback(async () => {
    if (isExporting || !report) return

    dispatch(reports.actions.setReportError({ status: false, type: null, data: null }))
    setIsExporting(true)
    setIsExportRendererMounted(true)

    try {
      await waitForExportRender()
      if (!exportRef.current) {
        throw new Error('AI Risk Report export renderer was not mounted')
      }

      await downloadAIRiskReportPDF({
        element: exportRef.current,
        customerName,
        branding: getPublishedBrandingLogo(brandingData),
        intl,
      })
      emitExportAuditLog()
    } catch (error) {
      console.error('Failed to export AI Risk Report PDF', error)
      dispatch(reports.actions.setReportError({ status: true, type: AI_RISK_REPORT_EXPORT_ERROR_TYPE }))
    } finally {
      setIsExportRendererMounted(false)
      setIsExporting(false)
    }
  }, [brandingData, customerName, dispatch, emitExportAuditLog, intl, isExporting, report])

  if (isLoading && !report) {
    return <Button variant="light" className="spinner spinner--center spinner--md spinner--primary" />
  }

  if (isError || !report) return null

  const exportControl = isExporting ? (
    <Button variant="light" className="spinner spinner--center spinner--md spinner--primary" />
  ) : (
    <Dropdown drop="down" className="ms-3 p-0">
      <Dropdown.Toggle variant="light" id="dropdownToggleExport">
        <FormattedMessage id="GENERAL.EXPORT" />
      </Dropdown.Toggle>
      <Dropdown.Menu className="dropdown-menu-fit dropdown-menu-end dropdown-menu-anim">
        <ul className="nav margin-t-10 margin-b-10">
          <li className="nav__item" data-bs-toggle="collapse">
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <span onClick={handleExport} className="nav__link" id="export-pdf-ai-risk-report">
              <span className="nav__link nav__link-text">
                <FormattedMessage id="GENERAL.PDF" />
              </span>
            </span>
          </li>
        </ul>
      </Dropdown.Menu>
    </Dropdown>
  )

  return (
    <>
      {exportControl}
      {isExportRendererMounted && (
        <div className={`${styles.exportHost} position-fixed`} aria-hidden="true">
          <div ref={exportRef} className={`${styles.exportDocument} min-vh-100`}>
            <AIRiskReportRenderer
              report={report}
              conditionalAccessPolicies={conditionalAccessPolicies}
              conditionalAccessPoliciesIsLoading={conditionalAccessPoliciesIsLoading}
              conditionalAccessPoliciesIsError={conditionalAccessPoliciesIsError}
              renderMode={AI_RISK_REPORT_RENDER_MODES.EXPORT}
            />
          </div>
        </div>
      )}
    </>
  )
}

AIRiskReportExportWidget.propTypes = {}

export default React.memo(AIRiskReportExportWidget)
